/* Study plan (#2): turns the signals Home already computes (target date, spaced-
 * review due, weakest module, diagnostic status) into a paced, phase-based daily
 * prescription. Pure + deterministic within a day — no backend, no LLM. */

export type PlanPhase = "foundation" | "build" | "sharpen" | "final";

export type PlanStepKey =
  | "diagnostic" | "review" | "weakness" | "practice_test" | "gauntlet" | "explore" | "cause";

export type PlanStep = { key: PlanStepKey; label: string; sub: string; to: string };

export type PlanInputs = {
  targetDate: string | null; // 'YYYY-MM' or null
  today: Date;
  hasDiagnostic: boolean;
  reviewDueTotal: number;
  reviewDueModule?: string;
  weakestModule?: string;
  weakestSkillLabel?: string;
  // First module of the user's chosen exam target, used as the "build breadth"
  // fallback before any diagnostic exists (so an ACT student isn't sent to SAT Reading).
  defaultModule?: string;
  /** Tagged miss reasons, if the user has tagged enough of them to be worth acting on. */
  missReasons?: MissReasonCounts;
};

export type StudyPlan = {
  phase: PlanPhase;
  weeksToTest: number | null;
  dailyGoalItems: number;
  steps: PlanStep[]; // ordered: what to do today, top to bottom
  /** The miss cause driving the plan, or null when too few tags to be meaningful. */
  cause: MissCause | null;
};

const DAY_MS = 86_400_000;

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** The target date as a concrete Date: exact for YYYY-MM-DD, mid-month for
 *  YYYY-MM (month granularity). null if unset/malformed. */
export function targetAnchor(targetDate: string | null): Date | null {
  const m = targetDate ? /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(targetDate) : null;
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, m[3] ? Number(m[3]) : 15);
}

/** Whole weeks from `today` to the target date. null if unset. */
export function weeksToTest(targetDate: string | null, today: Date): number | null {
  const anchor = targetAnchor(targetDate);
  if (!anchor) return null;
  const days = Math.round((anchor.getTime() - startOfDay(today).getTime()) / DAY_MS);
  return Math.max(0, Math.round(days / 7));
}

export function phaseFor(weeks: number | null): PlanPhase {
  if (weeks == null) return "build"; // no date set → steady build
  if (weeks > 6) return "foundation";
  if (weeks > 3) return "build";
  if (weeks > 1) return "sharpen";
  return "final";
}

/** Consecutive local days with >=1 attempt, counting back from today (a gap today
 *  is forgiven if yesterday was active — the streak is "still alive today"). */
export function activeStreak(attemptTimestamps: number[], today: Date): number {
  const days = new Set(
    attemptTimestamps.map((t) => startOfDay(new Date(t)).getTime()),
  );
  const t0 = startOfDay(today).getTime();
  let streak = 0;
  // Anchor: today if active, else yesterday (so an unstarted today doesn't zero it).
  let cursor = days.has(t0) ? t0 : t0 - DAY_MS;
  if (!days.has(cursor)) return 0;
  while (days.has(cursor)) {
    streak += 1;
    cursor -= DAY_MS;
  }
  return streak;
}

export function computePlan(inp: PlanInputs): StudyPlan {
  const weeks = weeksToTest(inp.targetDate, inp.today);
  const phase = phaseFor(weeks);
  const steps: PlanStep[] = [];

  if (!inp.hasDiagnostic) {
    steps.push({
      key: "diagnostic", label: "Take the diagnostic", to: "/app/diagnostic",
      sub: "A 15-question check maps where you stand across every skill.",
    });
  }
  if (inp.reviewDueTotal > 0 && inp.reviewDueModule) {
    steps.push({
      key: "review", label: `Review ${inp.reviewDueTotal} due`,
      to: `/app/drill/${inp.reviewDueModule}?mode=review`,
      sub: "Spaced review of items you've seen. This is what makes them stick.",
    });
  }
  // A named cause outranks "your weakest module": it says what KIND of work to
  // do, where the module only says where. Falls through to the old step when
  // too few misses are tagged for the split to mean anything.
  const cause = dominantCause(inp.missReasons);
  if (inp.hasDiagnostic && cause) {
    const work = CAUSE_WORK[cause];
    const mod = inp.weakestModule ?? inp.defaultModule ?? "trapline";
    steps.push({ key: "cause", label: work.label, sub: work.sub, to: work.to(mod) });
  } else if (inp.hasDiagnostic && inp.weakestModule) {
    steps.push({
      key: "weakness", label: "Drill your weak spot",
      to: `/app/drill/${inp.weakestModule}?mode=weakness`,
      sub: inp.weakestSkillLabel
        ? `Your thinnest area right now: ${inp.weakestSkillLabel}.`
        : "Targets your lowest-accuracy skill; includes ones you recently missed.",
    });
  }
  if (phase === "final") {
    steps.push({
      key: "practice_test", label: "Full practice test", to: "/app/practice-test",
      sub: "Test week. Run a full-length under the real clock.",
    });
  } else if (phase === "sharpen") {
    steps.push({
      key: "gauntlet", label: "Hard Math gauntlet", to: "/app/practice-test",
      sub: "22 of the hardest Math questions in 35 minutes, on the real clock.",
    });
  } else if (phase === "foundation") {
    steps.push({
      key: "explore", label: "Build breadth",
      to: `/app/drill/${inp.weakestModule ?? inp.defaultModule ?? "trapline"}`,
      sub: "Plenty of runway. Rotate through skills to build a base.",
    });
  }

  const dailyGoalItems = phase === "final" ? 30 : phase === "sharpen" ? 25 : 20;
  return { phase, weeksToTest: weeks, dailyGoalItems, steps, cause };
}

// ---- Trajectory projection (gap-backlog B4) --------------------------------

export type TrajectoryStatus = "ahead" | "on_track" | "behind";

export type Trajectory = {
  /** Projected total scaled score (400-1600) at the target date. */
  projected: number;
  goal: number;
  /** projected - goal. */
  delta: number;
  status: TrajectoryStatus;
  testsUsed: number;
};

/** Parse a goal band like "1400+" to its floor score. */
export function goalScore(band: string | null): number | null {
  const m = band ? /^(\d{3,4})\+$/.exec(band) : null;
  return m ? Number(m[1]) : null;
}

/**
 * Deterministic ahead/on-track/behind projection: least-squares fit of total
 * scaled score over time across completed standard practice tests, evaluated
 * at the target date. Hard-pool tests are excluded (not on the mixed curve).
 * Needs >=2 usable tests and a goal band + date; otherwise null.
 */
export function projectTrajectory(
  tests: Array<{ submitted_at: number; total_scaled: number; hard_mode?: boolean }>,
  targetBand: string | null,
  targetDate: string | null,
  today: Date,
): Trajectory | null {
  const goal = goalScore(targetBand);
  const anchor = targetAnchor(targetDate);
  if (goal == null || anchor == null) return null;
  const usable = tests.filter((t) => !t.hard_mode && Number.isFinite(t.total_scaled));
  if (usable.length < 2) return null;

  // Least squares over (days-since-first-test, score) — small numbers, stable.
  const t0 = usable[0]!.submitted_at;
  const xs = usable.map((t) => (t.submitted_at - t0) / DAY_MS);
  const ys = usable.map((t) => t.total_scaled);
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  const varX = xs.reduce((a, x) => a + (x - mx) * (x - mx), 0);
  // All tests same day → no slope information; project the mean forward.
  const slope = varX > 0 ? xs.reduce((a, x, i) => a + (x - mx) * (ys[i]! - my), 0) / varX : 0;
  const xTarget = (anchor.getTime() - t0) / DAY_MS;
  const raw = my + slope * (xTarget - mx);
  const projected = Math.round(Math.max(400, Math.min(1600, raw)) / 10) * 10;

  const delta = projected - goal;
  const status: TrajectoryStatus = delta >= 40 ? "ahead" : delta > -40 ? "on_track" : "behind";
  return { projected, goal, delta, status, testsUsed: usable.length };
}

// ---- Miss-cause allocation --------------------------------------------------
//
// The app has collected `{trap, careless, didnt_know, timing}` on every tagged
// wrong answer since the miss-reason buttons shipped, and until now only Mistake
// Review read them. That is the wrong consumer: the counts describe what kind of
// work is worth doing, which is a question about the PLAN. A student whose misses
// are mostly timing does not need more content revision, and the plan was
// recommending it anyway because it only ever looked at the weakest module.

export type MissReasonCounts = Partial<Record<MissCause, number>>;
export type MissCause = "trap" | "careless" | "didnt_know" | "timing";

/** Minimum tags before the split is worth acting on. Below this the sample is
 *  noise and the plan falls back to weakest-module targeting. */
export const MIN_TAGS_FOR_CAUSE = 12;

/** The dominant miss cause, or null when too few tags or no clear leader.
 *  "Clear" means a strict plurality: ties resolve to null rather than to
 *  whichever key happens to come first. */
export function dominantCause(counts: MissReasonCounts | undefined): MissCause | null {
  if (!counts) return null;
  const entries = (Object.entries(counts) as Array<[MissCause, number]>)
    .filter(([, n]) => Number.isFinite(n) && n > 0);
  const total = entries.reduce((a, [, n]) => a + n, 0);
  if (total < MIN_TAGS_FOR_CAUSE) return null;
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  if (sorted.length === 0) return null;
  if (sorted.length > 1 && sorted[0]![1] === sorted[1]![1]) return null; // tie
  return sorted[0]![0];
}

/** What each cause asks the student to actually do. Pre-authored, never generated. */
export const CAUSE_WORK: Record<MissCause, { label: string; sub: string; to: (m: string) => string }> = {
  trap: {
    label: "Work the answer choices",
    sub: "Most of your tagged misses were narrowed to two and lost. That is the choices, not the passage.",
    to: (m) => `/app/drill/${m}?mode=weakness`,
  },
  timing: {
    label: "Timed section",
    sub: "Most of your tagged misses ran out of time. Untimed sets do not rehearse that.",
    to: () => "/app/pace",
  },
  didnt_know: {
    label: "Learn what you missed",
    sub: "Most of your tagged misses were content you had not met. Revision is the right response here.",
    to: (m) => `/app/drill/${m}?mode=review`,
  },
  careless: {
    label: "Name the target first",
    sub: "Most of your tagged misses were slips. On Math these are often a clean answer to the wrong question.",
    to: (m) => `/app/drill/${m}?mode=weakness`,
  },
};

// ---- The visible arc --------------------------------------------------------

export type WeekState = "done" | "current" | "upcoming";
export type PlanWeek = {
  /** First week this row covers, 1-based. */
  from: number;
  /** Last week this row covers. Equal to `from` for a single-week row. */
  to: number;
  label: string;
  focus: string;
  detail: string;
  state: WeekState;
  /** Where this span's work happens, so the current week is startable. */
  to_href: string;
  /** Calendar range this span covers, e.g. "Aug 16 to Oct 10". Anchors the plan
   *  to the dates the student actually lives in rather than abstract week numbers. */
  dates: string;
};

/**
 * The whole run-up as a small number of SPANS, so a student sees the shape
 * rather than only today.
 *
 * Spans, not one row per week. The first version emitted a row per week and a
 * twelve-week runway rendered as ten byte-identical rows saying "work your
 * largest miss cause", which is noise wearing the costume of a plan. We do not
 * have week-by-week content to differentiate week 6 from week 7, so claiming to
 * would be padding. Three or four honest rows beat twelve repeated ones.
 *
 * A short runway also changes the plan in KIND, not degree. With two weeks there
 * is no room for two full tests plus a build phase, so the arc is triage:
 * measure once, spend the middle on the single largest cause, keep the last days
 * for timed work rather than new material.
 */
export function planArc(
  targetDate: string | null,
  today: Date,
  counts?: MissReasonCounts,
): PlanWeek[] | null {
  const weeks = weeksToTest(targetDate, today);
  if (weeks == null) return null;
  const total = Math.max(1, weeks);
  const cause = dominantCause(counts);
  const causeWord =
    cause === "trap" ? "the answer choices"
    : cause === "timing" ? "pacing"
    : cause === "didnt_know" ? "the content you missed"
    : cause === "careless" ? "checking the target"
    : "your largest miss cause";

  type Span = { from: number; to: number; focus: string; detail: string };
  const spans: Span[] = [];

  if (total === 1) {
    spans.push({ from: 1, to: 1, focus: "Test week",
      detail: "Short timed sections and rest. No new material and no full test in the last few days." });
  } else if (total === 2) {
    spans.push({ from: 1, to: 1, focus: "Measure",
      detail: "One full unseen test, then sort every miss by cause before reading any explanation." });
    spans.push({ from: 2, to: 2, focus: "Timed work",
      detail: "Short timed sections on the largest cause. No new material and no second full test this late." });
  } else {
    spans.push({ from: 1, to: 1, focus: "Measure",
      detail: "One full unseen test, then sort every miss by cause before reading any explanation." });
    // A long runway earns a breadth phase before narrowing; a short one does not.
    const workFrom = 2;
    const retest = total;
    if (total > 8) {
      const breadthTo = Math.min(total - 4, Math.floor(total / 2));
      spans.push({ from: workFrom, to: breadthTo, focus: "Build breadth",
        detail: "Plenty of runway. Rotate across skills to build a base before narrowing." });
      spans.push({ from: breadthTo + 1, to: retest - 1, focus: `Work ${causeWord}`,
        detail: "Timed sections rather than untimed sets, with some mixed practice so the rest stays warm." });
    } else {
      spans.push({ from: workFrom, to: retest - 1, focus: `Work ${causeWord}`,
        detail: "Timed sections rather than untimed sets, with some mixed practice so the rest stays warm." });
    }
    spans.push({ from: retest, to: retest, focus: "Retest",
      detail: "A second unseen test under the same conditions, sorted the same way, to see whether the cause moved." });
  }

  // Elapsed weeks are inferred from the runway remaining, so the arc needs no
  // stored start date and cannot drift out of sync with the target. Clamped into
  // [1, total]: on test day `weeks` is 0, which would otherwise put `current`
  // one past the final span and leave nothing marked current.
  const current = Math.min(total, Math.max(1, total - weeks + 1));
  // Week N counts back from the target, so week `total` ends on test day.
  const anchor = targetAnchor(targetDate)!;
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const weekStart = (n: number) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() - (total - n + 1) * 7 + 1);
    return d;
  };
  const weekEnd = (n: number) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() - (total - n) * 7);
    return d;
  };

  const HREF: Record<string, string> = {
    Measure: "/app/practice-test",
    Retest: "/app/practice-test",
    "Timed work": "/app/pace",
    "Test week": "/app/pace",
    "Build breadth": "/app/home",
  };

  return spans
    .filter((s) => s.to >= s.from)
    .map((s) => {
      const a = weekStart(s.from);
      const b = weekEnd(s.to);
      return {
        from: s.from,
        to: s.to,
        label: s.from === s.to ? `Week ${s.from}` : `Weeks ${s.from} to ${s.to}`,
        focus: s.focus,
        detail: s.detail,
        state: (s.to < current ? "done" : s.from <= current ? "current" : "upcoming") as WeekState,
        to_href: HREF[s.focus] ?? (cause ? CAUSE_WORK[cause].to("trapline") : "/app/home"),
        dates: fmt(a) === fmt(b) ? fmt(b) : `${fmt(a)} to ${fmt(b)}`,
      };
    });
}

export const PHASE_LABEL: Record<PlanPhase, string> = {
  foundation: "Foundation",
  build: "Building",
  sharpen: "Sharpening",
  final: "Test week",
};
